import { Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Spinner } from "./components/ui";
import { ScrollManager } from "./components/ScrollManager";
import { lazyWithRetry } from "./lib/lazyWithRetry";
import { postLoginPath } from "./lib/nav";
import { useAuth } from "./auth/AuthContext";
import LandingPage from "./pages/landing/LandingPage";
import {
  BatteryArbitragePage,
  EnergyIntelligencePage,
  TariffOptimizationPage,
} from "./pages/landing/ValuePages";
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ForgotPage from "./pages/auth/ForgotPage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import { AppLayout } from "./pages/app/AppLayout";
import SitesOverview from "./pages/app/SitesOverview";

const SiteDetail = lazyWithRetry(() => import("./pages/app/SiteDetail"));
const DeviceShell = lazyWithRetry(() => import("./device/DeviceShell"));
const SubscriptionPage = lazyWithRetry(() => import("./pages/app/SubscriptionPage"));
const PlanSelectionPage = lazyWithRetry(() => import("./pages/app/PlanSelectionPage"));
const AccountPage = lazyWithRetry(() => import("./pages/app/AccountPage"));
const FleetPage = lazyWithRetry(() => import("./pages/fleet/FleetPage"));
const AdminPage = lazyWithRetry(() => import("./pages/admin/AdminPage"));

function CenterSpinner() {
  return (
    <div className="flex items-center justify-center py-24 text-muted">
      <Spinner size={22} />
    </div>
  );
}

// Signed-out visitors go to /login carrying the page they asked for, so a
// deep link (an invite, a site tab) survives the sign-in. LoginPage only
// follows it to an in-app path (lib/nav.safeNextPath).
function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <CenterSpinner />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

// /login and /register while already signed in: continue to where the
// visitor was headed (validated), else the app.
function RedirectIfAuthed({ children }: { children: JSX.Element }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return children;
  const from = (location.state as { from?: unknown } | null)?.from;
  return <Navigate to={postLoginPath(from)} replace />;
}

export default function App() {
  return (
    <>
      <ScrollManager />
      <AppRoutes />
    </>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/tariff-optimization" element={<TariffOptimizationPage />} />
      <Route path="/battery-arbitrage" element={<BatteryArbitragePage />} />
      <Route path="/energy-intelligence" element={<EnergyIntelligencePage />} />
      <Route path="/login" element={<RedirectIfAuthed><LoginPage /></RedirectIfAuthed>} />
      <Route path="/register" element={<RedirectIfAuthed><RegisterPage /></RedirectIfAuthed>} />
      <Route path="/forgot" element={<ForgotPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route
        path="/app"
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route index element={<SitesOverview />} />
        <Route
          path="sites/:siteId/device/*"
          element={
            <Suspense fallback={<CenterSpinner />}>
              <DeviceShell />
            </Suspense>
          }
        />
        <Route
          path="sites/:siteId/:tab?"
          element={
            <Suspense fallback={<CenterSpinner />}>
              <SiteDetail />
            </Suspense>
          }
        />
        <Route
          path="fleet/:tab?"
          element={
            <Suspense fallback={<CenterSpinner />}>
              <FleetPage />
            </Suspense>
          }
        />
        <Route
          path="subscription"
          element={
            <Suspense fallback={<CenterSpinner />}>
              <SubscriptionPage />
            </Suspense>
          }
        />
        <Route
          path="subscription/plans"
          element={
            <Suspense fallback={<CenterSpinner />}>
              <PlanSelectionPage />
            </Suspense>
          }
        />
        <Route
          path="account"
          element={
            <Suspense fallback={<CenterSpinner />}>
              <AccountPage />
            </Suspense>
          }
        />
        <Route
          path="admin"
          element={
            <Suspense fallback={<CenterSpinner />}>
              <AdminPage />
            </Suspense>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
